import { Button } from "@/components/ui/button";
import { Link } from "wouter";
import { FileText, CheckCircle2, DollarSign, Clock, Shield } from "lucide-react";
import Footer from "@/components/Footer";
import { SiteNav } from "@/components/SiteNav";
import PageSEO from "@/components/PageSEO";
import { Helmet } from "react-helmet-async";

const JSON_LD = {
  "@context": "https://schema.org",
  "@graph": [
    {
      "@type": "Organization",
      "@id": "https://landpartner.co/#organization",
      name: "LandPartner",
      url: "https://landpartner.co",
      logo: "https://landpartner.co/img/logo.png",
      contactPoint: {
        "@type": "ContactPoint",
        contactType: "customer support",
        url: "https://landpartner.co/contact",
      },
    },
    {
      "@type": "WebSite",
      "@id": "https://landpartner.co/#website",
      url: "https://landpartner.co",
      name: "LandPartner",
      publisher: { "@id": "https://landpartner.co/#organization" },
    },
  ],
};

export default function LandingPage() {
  return (
    <div className="min-h-screen bg-background">
      <PageSEO
        title="LandPartner — Land Deal Funding for Wholesalers"
        description="Submit your vacant land deals and get them professionally reviewed and funded by LandPartner. Fast decisions, flexible structures, and direct funding from $500 to $500,000."
        path="/"
      />
      <Helmet>
        <script type="application/ld+json">{JSON.stringify(JSON_LD)}</script>
      </Helmet>
      <SiteNav />

      {/* Hero Section */}
      <section className="relative min-h-[700px] flex items-center justify-center overflow-hidden pt-24 sm:pt-28">
        <div className="absolute inset-0 z-0">
          <picture>
            <source type="image/avif"
              srcSet="/img/hero-640.avif 640w, /img/hero-960.avif 960w, /img/hero-1280.avif 1280w, /img/hero-1408.avif 1408w"
              sizes="100vw" />
            <source type="image/webp"
              srcSet="/img/hero-640.webp 640w, /img/hero-960.webp 960w, /img/hero-1280.webp 1280w, /img/hero-1408.webp 1408w"
              sizes="100vw" />
            <img
              src="/img/hero-1280.webp"
              alt="Land property aerial view"
              width={1408}
              height={768}
              {...{ fetchpriority: "high" } /* lowercase: React 18.3.1 warns on camelCase fetchPriority */}
              decoding="async"
              className="w-full h-full object-cover"
            />
          </picture>
          <div className="absolute inset-0 bg-black/40" />
        </div>
        <div className="relative z-10 max-w-4xl mx-auto px-4 py-20 text-center">
          <h1 className="text-5xl md:text-6xl font-bold text-white mb-6">
            Get Your Land Deal Funded & Reviewed by The Best in the Business!
          </h1>
          <p className="text-xl text-white/90 mb-8 max-w-2xl mx-auto">
            Submit your land deals and get them professionally reviewed and funded by LandPartner
          </p>
          <div className="flex justify-center">
            <Link href="/auth?tab=signup">
              <Button size="lg" className="px-8 bg-primary text-primary-foreground" data-testid="button-submit-deal">
                <FileText className="w-5 h-5 mr-2" />
                Submit Your Deal
              </Button>
            </Link>
          </div>
          <div className="mt-12 flex items-center justify-center gap-8 text-white/80 text-sm">
            <div className="flex items-center gap-2">
              <CheckCircle2 className="w-4 h-4" />
              <span>Fast funding decisions</span>
            </div>
            <div className="flex items-center gap-2">
              <CheckCircle2 className="w-4 h-4" />
              <span>Funding from $500 - $500,000</span>
            </div>
          </div>
        </div>
      </section>

      {/* Why Submit to LandPartner */}
      <section className="py-16 bg-background">
        <div className="max-w-4xl mx-auto px-4">
          <div className="text-center mb-12">
            <h2 className="text-4xl font-bold mb-4">Why Submit to LandPartner?</h2>
            <p className="text-muted-foreground text-lg">
              We fund land deals directly - no waiting for third-party investors
            </p>
          </div>
          <div className="grid md:grid-cols-3 gap-8">
            <div className="text-center space-y-4">
              <div className="inline-flex items-center justify-center w-16 h-16 rounded-lg bg-primary/10 text-primary">
                <DollarSign className="w-8 h-8" />
              </div>
              <h3 className="text-xl font-semibold">Direct Funding</h3>
              <p className="text-muted-foreground">
                We fund deals internally with our own capital - faster decisions, smoother process
              </p>
            </div>
            <div className="text-center space-y-4">
              <div className="inline-flex items-center justify-center w-16 h-16 rounded-lg bg-primary/10 text-primary">
                <Clock className="w-8 h-8" />
              </div>
              <h3 className="text-xl font-semibold">Quick Turnaround</h3>
              <p className="text-muted-foreground">
                Get your deal reviewed and funding decisions within days, not weeks
              </p>
            </div>
            <div className="text-center space-y-4">
              <div className="inline-flex items-center justify-center w-16 h-16 rounded-lg bg-primary/10 text-primary">
                <Shield className="w-8 h-8" />
              </div>
              <h3 className="text-xl font-semibold">Expert Review</h3>
              <p className="text-muted-foreground">
                Professional analysis and market comps review for every submitted deal
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Benefits */}
      <section id="what-you-get" className="py-16 bg-muted/30">
        <div className="max-w-4xl mx-auto px-4">
          <h2 className="text-3xl font-bold text-center mb-12">What You Get</h2>
          <div className="space-y-6">
            <div className="flex gap-4 items-start">
              <CheckCircle2 className="w-6 h-6 text-primary flex-shrink-0 mt-1" />
              <div>
                <h3 className="font-semibold text-lg mb-2">Professional Deal Analysis & Multiple Structure Options</h3>
                <p className="text-muted-foreground mb-3">
                  When you submit a deal, our expert team conducts a comprehensive review of the property, numbers, and opportunity. We analyze the market, run detailed comps, and evaluate every aspect of the deal to understand its true potential.
                </p>
                <p className="text-muted-foreground">
                  If the opportunity aligns with our investment criteria, we'll present you with 1-3 different deal structures tailored to your initial request and the specific characteristics of the property. Because every land deal is unique with varying types of upside potential, we customize our approach to maximize value for both parties.
                </p>
              </div>
            </div>
            <div className="flex gap-4 items-start">
              <CheckCircle2 className="w-6 h-6 text-primary flex-shrink-0 mt-1" />
              <div>
                <h3 className="font-semibold text-lg mb-2">Flexible Deal Structures</h3>
                <p className="text-muted-foreground mb-3">
                  Our structure options may include:
                </p>
                <ul className="text-muted-foreground space-y-2 ml-4">
                  <li className="flex items-start gap-2">
                    <span className="text-primary mt-1">•</span>
                    <span><strong>Profit Split</strong> - Partner with us and share the profits. Splits are time-incentivized — the faster the property sells, the larger your share, with the structure stepping down the longer it sits.</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="text-primary mt-1">•</span>
                    <span><strong>Full Buyout with Finder's Fee</strong> - We purchase the deal outright and pay you a finder's fee</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="text-primary mt-1">•</span>
                    <span><strong>Traditional Financing</strong> - Long-term lending options for the right opportunities</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="text-primary mt-1">•</span>
                    <span><strong>Creative Ownership Structures</strong> - Joint ventures, new entity formations, or custom arrangements based on the deal's unique characteristics</span>
                  </li>
                </ul>
              </div>
            </div>
            <div className="flex gap-4 items-start">
              <CheckCircle2 className="w-6 h-6 text-primary flex-shrink-0 mt-1" />
              <div>
                <h3 className="font-semibold text-lg mb-2">Professional Analysis Report (Even for Declined Deals)</h3>
                <p className="text-muted-foreground mb-3">
                  If a property doesn't meet our current investment criteria, we'll notify you promptly. However, you'll have the valuable option to purchase our comprehensive Deal Analysis Report for just $99.
                </p>
                <p className="text-muted-foreground mb-3">
                  This detailed report includes a personal video walkthrough where we explain exactly why we passed on the opportunity, highlighting potential issues, market concerns, or pricing challenges we identified. Many land investors find this professional insight invaluable before proceeding with a deal on their own.
                </p>
                <p className="text-muted-foreground">
                  Armed with our analysis, you can make informed decisions about whether to renegotiate terms with the seller, adjust your approach, or move on to better opportunities. It's like having an experienced partner review your deal - even when we don't fund it.
                </p>
              </div>
            </div>
            <div className="flex gap-4 items-start">
              <CheckCircle2 className="w-6 h-6 text-primary flex-shrink-0 mt-1" />
              <div>
                <h3 className="font-semibold text-lg mb-1">Transparent Process</h3>
                <p className="text-muted-foreground">
                  Track your deal status in real-time through your dashboard and stay informed every step of the way
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* How It Works */}
      <section className="py-16 bg-background">
        <div className="max-w-7xl mx-auto px-4">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-bold mb-4">How It Works</h2>
            <p className="text-muted-foreground text-lg max-w-2xl mx-auto">
              A simple 3-step process from submission to funding
            </p>
          </div>
          <div className="grid md:grid-cols-3 gap-8">
            <div className="text-center">
              <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-primary text-primary-foreground font-bold text-2xl mb-4">
                1
              </div>
              <h3 className="text-xl font-semibold mb-3">Submit Your Deal</h3>
              <p className="text-muted-foreground">
                Create an account and submit your land deal with all relevant documentation and pricing
              </p>
            </div>
            <div className="text-center">
              <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-primary text-primary-foreground font-bold text-2xl mb-4">
                2
              </div>
              <h3 className="text-xl font-semibold mb-3">We Review</h3>
              <p className="text-muted-foreground">
                Our team analyzes your deal, runs comps, and evaluates the opportunity
              </p>
            </div>
            <div className="text-center">
              <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-primary text-primary-foreground font-bold text-2xl mb-4">
                3
              </div>
              <h3 className="text-xl font-semibold mb-3">Get Funded</h3>
              <p className="text-muted-foreground">
                Approved deals receive funding offers and we move forward to closing
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Stats Section */}
      <section className="py-12 bg-background border-y border-border">
        <div className="max-w-7xl mx-auto px-4">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            <div className="text-center">
              <div className="flex items-center justify-center mb-2">
                <DollarSign className="w-8 h-8 text-primary" />
              </div>
              <p className="text-3xl font-bold">$Millions</p>
              <p className="text-sm text-muted-foreground">Capital Deployed</p>
            </div>
            <div className="text-center">
              <div className="flex items-center justify-center mb-2">
                <FileText className="w-8 h-8 text-primary" />
              </div>
              <p className="text-3xl font-bold">Thousands</p>
              <p className="text-sm text-muted-foreground">Deals Reviewed & Funded</p>
            </div>
            <div className="text-center">
              <div className="flex items-center justify-center mb-2">
                <Clock className="w-8 h-8 text-primary" />
              </div>
              <p className="text-3xl font-bold">7 Days</p>
              <p className="text-sm text-muted-foreground">Avg. Review Time</p>
            </div>
          </div>
        </div>
      </section>

      {/* Final CTA */}
      <section className="py-16 bg-background">
        <div className="max-w-4xl mx-auto px-4 text-center">
          <h2 className="text-4xl font-bold mb-4">Ready to Get Your Deal Funded?</h2>
          <p className="text-muted-foreground text-lg mb-8">
            Join an Elite group of wholesalers who have successfully funded their land deals with LandPartner
          </p>
          <Link href="/auth?tab=signup">
            <Button size="lg" data-testid="button-cta-submit">
              <FileText className="w-5 h-5 mr-2" />
              Submit Your Deal Now
            </Button>
          </Link>
        </div>
      </section>

      {/* Footer */}
      <Footer />
    </div>
  );
}
